import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  ButtonAsLink,
  IconName,
  Option,
  ProColors,
  Select,
} from '@treatwell/treatwell-aura';
import { css } from '@emotion/react';
import { debounce } from 'lodash';
import { useFormContext } from '../../state';
import { useIsMounted } from '../../hooks';
import {
  getPlaceAddress,
  getPlacePredictions,
} from '../../features/google-maps';

function AutocompleteResult(props: {
  prediction: google.maps.places.AutocompletePrediction;
}): JSX.Element {
  const { prediction } = props;

  return (
    <div>
      <div
        className="aura--copy-normal"
        css={css({ color: ProColors.navy[700] })}
      >
        {prediction.structured_formatting.main_text}
      </div>
      <div
        className="aura--copy-normal"
        css={css({ color: ProColors.navy[400] })}
      >
        {prediction.structured_formatting.secondary_text}
      </div>
    </div>
  );
}

function AddAnyway(props: { onAddAnyway: () => void }) {
  const { t } = useTranslation('stepOneSearch');

  return (
    <div
      className="aura--copy-sm"
      css={css({
        color: ProColors.navy[800],
        fontWeight: 400,
        whiteSpace: 'pre-wrap',
        display: 'flex',
      })}
    >
      <Trans
        t={t}
        i18nKey="addManually"
        components={[<ButtonAsLink onClick={props.onAddAnyway} size="small" />]}
      />
    </div>
  );
}

function useOptions(
  query: string,
  onAddAnyway: () => void,
): Option<google.maps.places.AutocompletePrediction | null | undefined>[] {
  const [options, setOptions] = useState<
    Option<google.maps.places.AutocompletePrediction | null | undefined>[]
  >([]);
  const ref = useRef<{ query: string; resultsQuery: string }>({
    query: '',
    resultsQuery: '',
  });
  const isMounted = useIsMounted();

  const tryUpdateOptions = useCallback(
    async (q: string) => {
      const predictions = await getPlacePredictions(q);
      if (!isMounted.current) {
        return;
      }

      if (
        // response does not match the current query
        !ref.current.query.startsWith(q) ||
        // is further away than the last response ?
        Math.abs(q.length - ref.current.query.length) >
          Math.abs(ref.current.resultsQuery.length - ref.current.query.length)
      ) {
        return;
      }

      setOptions(
        predictions.map(p => ({
          label: p.place_id,
          value: p,
          key: p.place_id,
          component: <AutocompleteResult prediction={p} />,
        })),
      );
      ref.current.resultsQuery = q;
    },
    [isMounted],
  );

  useEffect(() => {
    ref.current.query = query;

    if (query) {
      tryUpdateOptions(query);
    } else {
      setOptions([]);
      ref.current.resultsQuery = query;
    }
  }, [query, tryUpdateOptions]);

  return [
    ...options.slice(0, 3),
    {
      label: '',
      value: null,
      key: 'add_anyway',
      component: <AddAnyway onAddAnyway={onAddAnyway} />,
    },
  ];
}

export type SearchBusinessProps = {
  closeSearch: (addInfoMode?: 'manual' | 'automatic') => void;
};

export function SearchBusiness(props: SearchBusinessProps): JSX.Element {
  const { t } = useTranslation('input', { keyPrefix: 'searchBusiness' });
  const { setData } = useFormContext();
  const [query, setQuery] = useState<string>('');
  const { closeSearch } = props;

  const onAddAnyway = useCallback(() => {
    setData(current => {
      return {
        ...current,
        businessName: query,
      };
    });
    closeSearch('manual');
  }, [closeSearch, query, setData]);

  const onValueChange = useCallback(
    async (
      value: Option<
        google.maps.places.AutocompletePrediction | null | undefined
      >,
    ) => {
      if (!value.value) {
        return;
      }

      const address = await getPlaceAddress(value.value.place_id);
      setData(current => {
        return {
          ...current,
          businessName: value.value!.structured_formatting.main_text,
          address,
        };
      });
      closeSearch('automatic');
    },
    [closeSearch, setData],
  );

  const onInputChange = useCallback((q: string) => {
    setQuery(q);
  }, []);

  const options = useOptions(query, onAddAnyway);
  const value = useMemo(
    () => ({ label: query, value: undefined, key: 'default' }),
    [query],
  );

  return (
    <Select
      id="search-business"
      label={t('label')}
      iconName={IconName.TW_PRO_VENUE_SEARCH}
      options={options}
      value={value}
      onChange={onValueChange}
      filterOptionsFn={() => true}
      onInputChange={debounce(onInputChange, 300)}
      autoClearInput={false}
      css={css({
        '.aura--select-options': { maxHeight: 325 },
        '.aura--select-options-section': { height: 320 },
      })}
    />
  );
}
